import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import { Doughnut } from 'react-chartjs-2';
import StatCard from '../components/StatCard';
import { Td, TdBold, TdSmall, Badge } from '../components/RankingTable';
import CardSkeleton, { MiniCardSkeleton } from '../components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import { Calendar, MapPin, Users } from 'lucide-react';
import dashboardStatistics from 'api/requests/dashboardStatistics';

export default function EventosTab({ periodParams }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!periodParams) return;

        setIsLoading(true);
        dashboardStatistics.getEventos(periodParams)
            .then(response => setData(response.data))
            .catch(error => console.error('Erro ao carregar eventos:', error))
            .finally(() => setIsLoading(false));
    }, [periodParams]);

    if (isLoading) {
        return (
            <Container>
                <TopGrid>
                    <CardSkeleton height={200} />
                    <CardSkeleton height={200} />
                </TopGrid>
                <CardSkeleton height={140} />
                <HighlightsGrid>
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                </HighlightsGrid>
                <BottomGrid>
                    <CardSkeleton height={300} />
                    <CardSkeleton height={300} />
                </BottomGrid>
            </Container>
        );
    }

    if (!data) return null;

    const { status, proximoEvento, performance, topEventos, interesses, maiorParticipantes, maiorCaptacao, maiorInteracao, menorInteracao } = data;

    const performanceChartData = {
        labels: performance.labels,
        datasets: [
            {
                label: 'Participantes',
                data: performance.participantes,
                backgroundColor: 'rgba(128, 37, 232, 0.15)',
                borderColor: '#8025e8',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 5,
            },
        ],
    };

    const performanceOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                },
                gridLines: {
                    color: '#f0f0f0',
                    drawBorder: false,
                },
            }],
            xAxes: [{
                ticks: {
                    fontColor: '#999',
                    fontSize: 12,
                },
                gridLines: {
                    display: false,
                },
            }],
        },
        legend: {
            display: false,
        },
        tooltips: {
            backgroundColor: '#1A1A1A',
            titleFontSize: 13,
            bodyFontSize: 12,
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
        },
    };

    const interessesChartData = {
        labels: interesses.map(i => i.nome),
        datasets: [
            {
                data: interesses.map(i => i.percentual),
                backgroundColor: interesses.map(i => i.cor),
                borderWidth: 0,
                hoverBorderWidth: 2,
                hoverBorderColor: '#fff',
            },
        ],
    };

    const interessesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: {
            display: false,
        },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: function (tooltipItem, data) {
                    return ` ${data.labels[tooltipItem.index]}: ${data.datasets[0].data[tooltipItem.index]}%`;
                },
            },
        },
    };

    return (
        <Container>
            <TopGrid>
                <StatCard title="Status dos Eventos">
                    <StatusList>
                        <StatusItem>
                            <StatusLabel>Em andamento</StatusLabel>
                            <StatusValue>{status.emAndamento}</StatusValue>
                        </StatusItem>
                        <StatusDivider />
                        <StatusItem>
                            <StatusLabel>Finalizados</StatusLabel>
                            <StatusValue>{status.finalizados}</StatusValue>
                        </StatusItem>
                        <StatusDivider />
                        <StatusItem>
                            <StatusLabel>Proximos</StatusLabel>
                            <StatusBadge>{status.proximos}</StatusBadge>
                        </StatusItem>
                    </StatusList>
                </StatCard>

                <StatCard title="Performance dos Eventos" subtitle="Eventos, participantes e receita por mes">
                    <ChartContainer>
                        <Line data={performanceChartData} options={performanceOptions} />
                    </ChartContainer>
                </StatCard>
            </TopGrid>

            <ProximoEventoCard>
                <StatCard title="Proximo Evento">
                    {proximoEvento ? (
                        <EventoInfo>
                            <EventoNome>{proximoEvento.nome}</EventoNome>
                            <EventoDetail>
                                <MapPin size={14} color={colors.night} />
                                <span>{proximoEvento.cidade}</span>
                            </EventoDetail>
                            <EventoDetail>
                                <Calendar size={14} color={colors.night} />
                                <span>{proximoEvento.data}</span>
                            </EventoDetail>
                            <InscritosBadge>
                                <Users size={14} color={colors.secondaryPurple} />
                                {proximoEvento.inscritos}
                            </InscritosBadge>
                        </EventoInfo>
                    ) : (
                        <EmptyMessage>Nenhum evento proximo agendado.</EmptyMessage>
                    )}
                </StatCard>
            </ProximoEventoCard>

            <HighlightsGrid>
                <HighlightItem>
                    <HighlightLabel>Evento com maior n de participantes</HighlightLabel>
                    <HighlightValue>{maiorParticipantes?.nome || '-'}</HighlightValue>
                    <HighlightSub>{maiorParticipantes ? `${maiorParticipantes.total.toLocaleString('pt-BR')} participantes` : '-'}</HighlightSub>
                </HighlightItem>
                <HighlightItem>
                    <HighlightLabel>Evento com maior captacao financeira</HighlightLabel>
                    <HighlightValue>{maiorCaptacao?.nome || '-'}</HighlightValue>
                    <HighlightSub>{maiorCaptacao ? currency.format(maiorCaptacao.valor) : '-'}</HighlightSub>
                </HighlightItem>
                <HighlightItem>
                    <HighlightLabel>Maior interacao</HighlightLabel>
                    <HighlightValue>{maiorInteracao?.nome || '-'}</HighlightValue>
                    <HighlightSub>{maiorInteracao ? `${maiorInteracao.percentual}% de interacao` : '-'}</HighlightSub>
                </HighlightItem>
                <HighlightItem>
                    <HighlightLabel>Menor interacao</HighlightLabel>
                    <HighlightValue>{menorInteracao?.nome || '-'}</HighlightValue>
                    <HighlightSub>{menorInteracao ? `${menorInteracao.percentual}% de interacao` : '-'}</HighlightSub>
                </HighlightItem>
            </HighlightsGrid>

            <BottomGrid>
                <StatCard title="Top Eventos" subtitle="Eventos com melhor performance">
                    {topEventos.map((evento, index) => (
                        <TopEventoItem key={index}>
                            <TopEventoLeft>
                                <TopEventoNome>{evento.nome}</TopEventoNome>
                                <TopEventoSub>
                                    {evento.participantes.toLocaleString('pt-BR')} participantes • {currency.format(evento.receita)}
                                </TopEventoSub>
                            </TopEventoLeft>
                            <Badge>{evento.interacao}% interacao</Badge>
                        </TopEventoItem>
                    ))}
                </StatCard>

                <StatCard title="Interesses dos Participantes" subtitle="Categorias mais populares">
                    <InteressesContent>
                        <DoughnutWrap>
                            <Doughnut data={interessesChartData} options={interessesOptions} />
                        </DoughnutWrap>
                        <LegendList>
                            {interesses.map((item, index) => (
                                <LegendItem key={index}>
                                    <LegendLeft>
                                        <LegendDot color={item.cor} />
                                        <LegendLabel>{item.nome}</LegendLabel>
                                    </LegendLeft>
                                    <LegendValue>{item.percentual}%</LegendValue>
                                </LegendItem>
                            ))}
                        </LegendList>
                    </InteressesContent>
                </StatCard>
            </BottomGrid>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const TopGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const StatusList = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatusItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
`;

const StatusDivider = styled.div`
    height: 1px;
    background: #f0f0f0;
`;

const StatusLabel = styled.span`
    font-size: 14px;
    color: ${colors.nightDark};
`;

const StatusValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    background: #F3E8FF;
    color: ${colors.secondaryPurple};
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
`;

const ChartContainer = styled.div`
    height: 220px;
    width: 100%;
`;

const ProximoEventoCard = styled.div``;

const EmptyMessage = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0;
    padding: 12px 0;
`;

const EventoInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const EventoNome = styled.h4`
    font-size: 16px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
`;

const EventoDetail = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${colors.night};
`;

const InscritosBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #F3E8FF;
    color: ${colors.secondaryPurple};
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    width: fit-content;
    margin-top: 4px;
`;

const HighlightsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const HighlightItem = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #f0f0f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const HighlightLabel = styled.div`
    font-size: 12px;
    color: ${colors.night};
    margin-bottom: 8px;
    font-weight: 500;
`;

const HighlightValue = styled.div`
    font-size: 15px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin-bottom: 4px;
`;

const HighlightSub = styled.div`
    font-size: 13px;
    color: ${colors.secondaryPurple};
    font-weight: 500;
`;

const BottomGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const TopEventoItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
`;

const TopEventoLeft = styled.div``;

const TopEventoNome = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const TopEventoSub = styled.div`
    font-size: 12px;
    color: ${colors.night};
    margin-top: 3px;
`;

const InteressesContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
`;

const DoughnutWrap = styled.div`
    width: 180px;
    height: 180px;
`;

const LegendList = styled.div`
    width: 100%;
`;

const LegendItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
`;

const LegendLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LegendDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;

const LegendLabel = styled.span`
    font-size: 14px;
    color: ${colors.nightDark};
`;

const LegendValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;
